import React, { useState } from "react";
import {
  Card,
  Button,
  Input,
  Space,
  Modal,
  Form,
  Select,
  Switch,
  message,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UpOutlined,
  DownOutlined,
  HolderOutlined,
} from "@ant-design/icons";

const QUESTION_TYPES = [
  { value: "single_choice", label: "Single choice" },
  { value: "multiple_choice", label: "Multiple choice" },
  { value: "text", label: "Short text" },
  { value: "rating", label: "Star rating (1-5)" },
];

const generateId = () => `q-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const FormBuilder = ({ questions = [], onChange, disabled = false }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [form] = Form.useForm();

  const sortedQuestions = [...questions].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const handleAdd = () => {
    setEditingQuestion(null);
    form.resetFields();
    form.setFieldsValue({
      type: "single_choice",
      required: true,
      options: ["Option 1"],
    });
    setModalVisible(true);
  };

  const handleEdit = (q) => {
    setEditingQuestion(q);
    form.setFieldsValue({
      question: q.question || "",
      type: q.type || "single_choice",
      required: q.required !== false,
      options: Array.isArray(q.options) && q.options.length > 0
        ? q.options
        : ["Option 1"],
    });
    setModalVisible(true);
  };

  const handleDelete = (id) => {
    const updated = questions.filter((q) => q.id !== id);
    onChange(updated);
  };

  const moveUp = (index) => {
    if (index <= 0) return;
    const updated = [...sortedQuestions];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    const withOrder = updated.map((q, i) => ({ ...q, order: i }));
    onChange(withOrder);
  };

  const moveDown = (index) => {
    if (index >= sortedQuestions.length - 1) return;
    const updated = [...sortedQuestions];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    const withOrder = updated.map((q, i) => ({ ...q, order: i }));
    onChange(withOrder);
  };

  const handleModalSubmit = () => {
    form.validateFields().then((values) => {
      const { question, type, required, options } = values;
      const needsOptions = type === "single_choice" || type === "multiple_choice";
      let opts = [];
      if (needsOptions) {
        opts = Array.isArray(options)
          ? options.map((o) => String(o || "").trim()).filter(Boolean)
          : [String(options || "").trim()].filter(Boolean);
        if (opts.length === 0) opts = ["Option 1"];
      }

      if (editingQuestion) {
        const updated = questions.map((q) =>
          q.id === editingQuestion.id
            ? { ...q, question: question.trim(), type, required, options: opts }
            : q
        );
        onChange(updated);
      } else {
        if (questions.length >= 10) {
          message.warning("Maximum 10 questions allowed.");
          return;
        }
        const newQ = {
          id: generateId(),
          order: questions.length,
          question: question.trim(),
          type,
          required,
          options: opts,
        };
        onChange([...questions, newQ]);
      }
      setModalVisible(false);
      form.resetFields();
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-600">
          {sortedQuestions.length} question{sortedQuestions.length !== 1 ? "s" : ""}
        </span>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
          disabled={disabled || sortedQuestions.length >= 10}
          size="small"
        >
          Add Question
        </Button>
      </div>

      {sortedQuestions.length === 0 ? (
        <div className="text-center py-6 border border-dashed border-gray-300 rounded-lg bg-gray-50">
          <p className="text-gray-500 mb-2">No questions yet</p>
          <p className="text-sm text-gray-400 mb-3">
            Add questions to collect feedback. Answers will be used to generate a personalized review.
          </p>
          <Button type="dashed" icon={<PlusOutlined />} onClick={handleAdd}>
            Add first question
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {sortedQuestions.map((q, index) => (
            <Card
              key={q.id}
              size="small"
              className="border border-gray-200 hover:border-primary"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <HolderOutlined className="text-gray-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 truncate">
                      {q.question || "Untitled question"}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {QUESTION_TYPES.find((t) => t.value === q.type)?.label || q.type}
                      {q.required && " • Required"}
                    </p>
                  </div>
                </div>
                <Space size="small" className="flex-shrink-0">
                  <Button
                    type="text"
                    size="small"
                    icon={<UpOutlined />}
                    onClick={() => moveUp(index)}
                    disabled={index === 0 || disabled}
                    title="Move up"
                  />
                  <Button
                    type="text"
                    size="small"
                    icon={<DownOutlined />}
                    onClick={() => moveDown(index)}
                    disabled={index === sortedQuestions.length - 1 || disabled}
                    title="Move down"
                  />
                  <Button
                    type="text"
                    size="small"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(q)}
                    disabled={disabled}
                  />
                  <Button
                    type="text"
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleDelete(q.id)}
                    disabled={disabled}
                  />
                </Space>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        title={editingQuestion ? "Edit Question" : "Add Question"}
        open={modalVisible}
        onOk={handleModalSubmit}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        okText="Save"
        destroyOnClose
      >
        <Form form={form} layout="vertical" initialValues={{ type: "single_choice", required: true }}>
          <Form.Item
            name="question"
            label="Question"
            rules={[{ required: true, message: "Enter the question text" }]}
          >
            <Input placeholder="e.g. How was your experience with our service?" className="input-border" />
          </Form.Item>
          <Form.Item name="type" label="Answer type" rules={[{ required: true }]}>
            <Select options={QUESTION_TYPES} />
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prev, curr) => prev.type !== curr.type}
          >
            {({ getFieldValue }) => {
              const type = getFieldValue("type");
              const needsOptions = type === "single_choice" || type === "multiple_choice";
              if (!needsOptions) return null;
              return (
                <Form.Item
                  name="options"
                  label="Options (one per line or comma-separated)"
                  rules={[
                    {
                      required: needsOptions,
                      message: "Add at least one option",
                    },
                  ]}
                >
                  <Select
                    mode="tags"
                    tokenSeparators={[",", "\n"]}
                    placeholder="Add options"
                    options={[]}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
          <Form.Item name="required" label="Required" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FormBuilder;
